import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { useRippleEffect } from '../../hooks/useRippleEffect';
import {
  InputEventKey,
  isActionKey,
} from '../../utils/htmlInputEventKeys';
import {
  snapToInterval,
  validateInput,
} from '../../utils/interestRateInput';
import styles from './InterestRateInput.module.less';

interface InterestRateInputProps {
  value: string;
  onChange: (newValue: string | number) => void;
  step?: number;
  minRate: number;
  maxRate: number;
}

const DEFAULT_STEP = 0.5;

const RIPPLE_DURATION = 600;
const MAX_RIPPLES_PER_BUTTON = 3;

export const InterestRateInput = ({
  value,
  onChange,
  step = DEFAULT_STEP,
  minRate,
  maxRate,
}: InterestRateInputProps) => {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [inputWidth, setInputWidth] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const cancelBlurSaveRef = useRef(false);
  const lastClickTimeRef = useRef(0);
  const { ripples, createRipple } = useRippleEffect<'minus' | 'plus'>(
    RIPPLE_DURATION,
    MAX_RIPPLES_PER_BUTTON
  );

  // Debounce to prevent double-firing on Firefox mobile
  const isDoubleFireClick = () => {
    const now = Date.now();
    if (now - lastClickTimeRef.current < 50) {
      return true;
    }
    lastClickTimeRef.current = now;
    return false;
  };

  const handleDecrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDoubleFireClick()) {
      return;
    }
    createRipple('minus');
    const currentValue = parseFloat(value) || 0;
    const newValue = snapToInterval(currentValue, step, 'down', minRate, maxRate);
    onChange(newValue);
  };

  const handleIncrement = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDoubleFireClick()) {
      return;
    }
    createRipple('plus');
    const currentValue = parseFloat(value) || 0;
    const newValue = snapToInterval(currentValue, step, 'up', minRate, maxRate);
    onChange(newValue);
  };

  const updateInputWidth = (val: string) => {
    if (!measureRef.current) {
      return 20;
    }

    measureRef.current.textContent = val || '0';
    const width = measureRef.current.offsetWidth;
    const newWidth = Math.max(20, width + 4);
    setInputWidth(newWidth);
    return newWidth;
  };

  const enterEditMode = () => {
    const formattedValue = String(parseFloat(value) || 0);
    setEditValue(formattedValue);
    updateInputWidth(formattedValue);
    setIsEditing(true);
    cancelBlurSaveRef.current = false;

    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const exitEditMode = (shouldSave: boolean) => {
    if (shouldSave && validateInput(editValue)) {
      const parsed = parseFloat(editValue.replace(',', '.'));
      const clamped = Math.min(Math.max(parsed, minRate), maxRate);
      updateInputWidth(String(clamped));
      onChange(clamped);
    } else {
      const formattedValue = String(parseFloat(value) || 0);
      updateInputWidth(formattedValue);
    }
    setIsEditing(false);
  };

  useLayoutEffect(() => {
    const formattedValue = String(parseFloat(value) || 0);
    updateInputWidth(formattedValue);
  }, [value, intl]);

  // Mobile view modal needs ResizeObserver to update interest rate element width
  // upon becoming visible
  useEffect(() => {
    const element = measureRef.current;
    if (!element) {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (element.offsetWidth > 0) {
        const width = Math.max(20, element.offsetWidth + 4);
        setInputWidth(width);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const inputValue = input.value;

    if (inputValue === '') {
      setEditValue('');
      updateInputWidth('');
      return;
    }

    if (inputValue.length > 6 || !validateInput(inputValue)) {
      // Reset input to current valid value if validation fails
      input.value = editValue;
      return;
    }

    setEditValue(inputValue);
    updateInputWidth(inputValue);
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isEditing) {
      enterEditMode();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isActionKey(e.key)) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (e.key === InputEventKey.ENTER) {
      exitEditMode(true);
      inputRef.current?.blur();
    } else if (e.key === InputEventKey.ESCAPE) {
      cancelBlurSaveRef.current = true;
      exitEditMode(false);
      inputRef.current?.blur();
    } else if (
      e.key === InputEventKey.ARROW_UP ||
      e.key === InputEventKey.ARROW_DOWN
    ) {
      const currentValue = parseFloat(editValue);
      const newValue = snapToInterval(
        currentValue,
        step,
        e.key === InputEventKey.ARROW_UP ? 'up' : 'down',
        minRate,
        maxRate
      );
      if (isEditing) {
        const formattedValue = String(newValue);
        setEditValue(formattedValue);
        updateInputWidth(formattedValue);
        setTimeout(() => inputRef.current?.select(), 0);
      }
    }
  };

  const handleBlur = () => {
    if (!isEditing) {
      return;
    }

    const shouldSave = !cancelBlurSaveRef.current;
    exitEditMode(shouldSave);
    cancelBlurSaveRef.current = false;
  };

  const displayValue = isEditing
    ? editValue
    : String(parseFloat(value) || 0);

  return (
    <div className={styles.interestRateInputContainer}>
      {/* Hidden span for measuring text width */}
      <span ref={measureRef} className={styles.measureSpan} aria-hidden="true">
        {String(parseFloat(value) || 0)}
      </span>

      <button
        className={styles.rateButton}
        onClick={handleDecrement}
        type="button"
      >
        <span>-</span>
        {ripples
          .filter(r => r.identifier === 'minus')
          .map(ripple => (
            <span key={ripple.id} className={styles.ripple} />
          ))}
      </button>

      <div
        className={`${styles.rateInputWrapper} ${
          isEditing ? styles.editing : ''
        }`}
        onClick={handleWrapperClick}
      >
        <input
          ref={inputRef}
          className={`${styles.rateInput} ${isEditing ? styles.editing : ''}`}
          type="text"
          value={displayValue}
          onInput={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={() => {
            if (!isEditing) {
              enterEditMode();
            }
          }}
          onClick={e => {
            e.stopPropagation();
            if (!isEditing) {
              enterEditMode();
            }
          }}
          style={{
            width: inputWidth > 0 ? `${inputWidth}px` : 'auto',
            marginLeft: inputWidth > 0 ? `${36 - inputWidth}px` : '0',
          }}
        />
        <span
          className={`${styles.percentSymbol} ${
            isEditing ? styles.hidden : ''
          }`}
        >
          %
        </span>
        <span
          className={`${styles.perAnnumInterest} ${
            isEditing ? styles.hidden : ''
          }`}
        >
          {intl.formatMessage({ id: 'interestRate.perAnnum' })}
        </span>
      </div>

      <button
        className={styles.rateButton}
        onClick={handleIncrement}
        type="button"
      >
        <span>+</span>
        {ripples
          .filter(r => r.identifier === 'plus')
          .map(ripple => (
            <span key={ripple.id} className={styles.ripple} />
          ))}
      </button>
    </div>
  );
};
